import React, { Component } from 'react'
import styled from 'styled-components'
import onClickOutside from 'react-onclickoutside'

import { palette, mediaQueries } from '../../styles/variables'
import { ArrowIcon, CheckMark } from '../icon/Icon'
import TextOverflow from '../textoverflow/TextOverflow'

const DropDownSelectorWrapper = styled.div((props) => ({
  position: 'relative',
  zIndex: 'auto',
  cursor: 'pointer',
  border: props.opened
    ? `1px solid ${props.activeColor || palette.lightGray20}`
    : `1px solid ${props.borderColor || palette.lightGray20}`,
  marginBottom: props?.customStyles?.containerMarginBottom
    ? props.customStyles.containerMarginBottom
    : '10px',
  width: props.width ? props.width : '100%',
  borderRadius: '2px',
}))

const SelectedOption = styled.div((props) => {
  if (props.opened) {
    return {
      borderRadius: '2px 2px 0 0',
      backgroundColor: palette.white,
      color: palette.darkGray70,
      boxShadow: '0px 10px 15px 5px rgba(19,23,35,0.04)',
      '& .arrow': {
        transform: 'translate(6px, 1px) rotate(135deg)',
      },
      '& ~ div': {
        borderRadius: '0px 2px 2px 2px',
      },
      [mediaQueries.mobile]: {
        display: 'flex',
        alignItems: 'center',
        margin: 0,
      },
    }
  }
  return {
    borderRadius: '2px',
    color: palette.darkGray,
    backgroundColor: palette.white,
    overflow: 'hidden',
    [mediaQueries.mobile]: {
      display: 'flex',
      alignItems: 'center',
      margin: 0,
    },
  }
})

const OptionWrapper = styled.div((props) => ({
  display: 'flex',
  width: '100%',
  alignItems: 'center',
  color: props.activeColor || 'inherit',
  textTransform: props.capitalize ? 'capitalize' : 'inherit',
  borderBottom: props.activeColor ? `solid 1px ${palette.lightGray20}` : 'none',
  '& > span': {
    marginRight: '20px',
  },
  [mediaQueries.mobile]: {
    width: '100%',
  },
}))

const OptionName = styled.div((props) => ({
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  fontSize: '15px',
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  padding: '10px 20px',
  minHeight: props?.style?.containerHeight
    ? props.style.containerHeight
    : '48px',

  color:
    props.showCheckMark && props.activeColor
      ? `${props.activeColor} !important`
      : 'inherit',
  [mediaQueries.smallMobile]: {
    fontSize: props?.style?.fontSize ? props.style.fontSize : '14px',
    padding: '0 10px',
  },
}))

const OptionDropdown = styled.div(() => ({
  position: 'absolute',
  zIndex: 102,
  marginTop: '8px',
  width: '100%',
  background: palette.white,
  boxShadow: '0px 14px 15px 5px rgba(19,23,35,0.04)',
  borderRadius: '2px',
  overflow: 'auto',
  [mediaQueries.mobile]: {
    width: '100%',
    marginLeft: 0,
  },
}))

const OptionList = styled.div({
  width: '100%',
  maxHeight: '230px',
  overflowY: 'auto',
  '& div': {
    color: palette.darkGray70,
  },
  '& div:hover': {
    color: palette.darkGray70,
    background: palette.lightGray20,
    cursor: 'pointer',
  },
  [mediaQueries.mobile]: {
    width: '100%',
  },
})

const Option = ({
  onClick,
  activeColor,
  capitalize,
  showCheckMark,
  customStyles,
  label,
  showArrow,
  arrowColor,
  testId,
}) => {
  return (
    <OptionWrapper
      onClick={onClick}
      activeColor={activeColor}
      capitalize={capitalize}
      data-testid={testId}
    >
      <OptionName
        activeColor={activeColor}
        showCheckMark={showCheckMark}
        style={customStyles}
      >
        <TextOverflow
          color={showCheckMark && activeColor ? activeColor : 'inherit'}
          display="block"
          text={label}
        />
      </OptionName>
      {showArrow && (
        <ArrowIcon
          down
          className="arrow"
          color={arrowColor || activeColor}
          mobileDisplay="none"
        />
      )}
      {showCheckMark && <CheckMark color={activeColor} mobileDisplay="none" />}
    </OptionWrapper>
  )
}

class DropdownSelector extends Component {
  constructor(props) {
    super(props)

    const { options, value } = props

    this.state = {
      isOpen: false,
      currentOption:
        value !== undefined && value !== null ? options[value] : null,
    }
  }

  // We need this disabled because the handleClickOutside is actually used by the react-onclickoutside library
  // eslint-disable-next-line react/no-unused-class-component-methods
  handleClickOutside = () => {
    this.setState({ isOpen: false })
  }

  selectOption = (option) => {
    this.setState({
      isOpen: false,
      currentOption: option,
    })
    if (this.props.onChange) {
      this.props.onChange(option.value)
    }
  }

  openList = () => {
    this.setState((prevState) => ({ isOpen: !prevState.isOpen }))
  }

  render() {
    const { isOpen, currentOption } = this.state
    const {
      options,
      placeholder,
      width,
      activeColor,
      borderColor,
      capitalize,
      arrowColor,
      testId = '',
      customStyles,
    } = this.props

    if (isOpen) {
      return (
        <DropDownSelectorWrapper
          data-testid={testId}
          width={width}
          opened
          activeColor={activeColor}
          borderColor={borderColor}
          customStyles={customStyles}
        >
          <SelectedOption onClick={this.openList} opened>
            {currentOption ? (
              <Option
                value={currentOption.value}
                label={currentOption.label}
                showArrow
                arrowColor={arrowColor}
                capitalize={capitalize}
                selectedOption
                customStyles={customStyles}
              />
            ) : (
              <Option
                value={null}
                label={placeholder}
                showArrow
                activeColor={borderColor}
                arrowColor={arrowColor}
                selectedOption
                customStyles={customStyles}
              />
            )}
          </SelectedOption>
          <OptionDropdown activeColor={activeColor}>
            <OptionList>
              {options.map((item, index) => (
                <Option
                  key={item.id || `option-${index}`}
                  value={item.value}
                  label={item.label}
                  onClick={() => this.selectOption(item)}
                  showCheckMark={
                    currentOption && currentOption.value === item.value
                  }
                  activeColor={activeColor}
                  arrowColor={arrowColor}
                  capitalize={capitalize}
                  customStyles={customStyles}
                />
              ))}
            </OptionList>
          </OptionDropdown>
        </DropDownSelectorWrapper>
      )
    }
    return (
      <DropDownSelectorWrapper
        data-testid={testId}
        width={width}
        activeColor={activeColor}
        borderColor={borderColor}
        customStyles={customStyles}
      >
        <SelectedOption onClick={this.openList}>
          {currentOption ? (
            <Option
              value={currentOption.value}
              label={currentOption.label}
              showArrow
              arrowColor={arrowColor}
              capitalize={capitalize}
              selectedOption
              customStyles={customStyles}
            />
          ) : (
            <Option
              value={null}
              label={placeholder}
              showArrow
              activeColor={borderColor}
              arrowColor={arrowColor}
              selectedOption
              customStyles={customStyles}
            />
          )}
        </SelectedOption>
      </DropDownSelectorWrapper>
    )
  }
}

export default onClickOutside(DropdownSelector)
